import { checkAnomaly, warmupScoringLambdas } from '../api/weldiqLiveApi';
import type { WeldDrillDownRow } from '../components/dashboard/drilldown/drillDownTypes';
import { runWithConcurrency } from './runWithConcurrency';
import { rowKey } from './weldDrillDownModel';
import {
  REVIEW_PRIORITY_RANK,
  reviewPriorityFromAnomalies,
} from './weldReviewPriority';
import { WPS_BATCH_FETCH_CAP } from './useWpsViolationsBatch';

/**
 * Out-of-limit live checks are Lambda calls (~tens of seconds each).
 * 2-wide left a 100-weld drill-down running for many minutes; 4 keeps
 * the gateway from being stampeded while the counter actually moves.
 */
export const LIVE_ANOMALY_PREFETCH_CONCURRENCY = 4;

export interface PrefetchLiveAnomalyScoresArgs {
  rows: WeldDrillDownRow[];
  projectId: number;
  /** Keys that already have an anomaly result (or in-flight from UI). */
  skipKeys: Set<string>;
  onProgress?: (loaded: number, total: number) => void;
  onResult: (weldKey: string, result: Record<string, unknown>) => void;
  onError: (weldKey: string, message: string) => void;
  shouldContinue?: () => boolean;
  concurrency?: number;
  cap?: number;
}

function sortFailsByPriority(rows: WeldDrillDownRow[]): WeldDrillDownRow[] {
  return [...rows].sort((a, b) => {
    const pa = reviewPriorityFromAnomalies(a.anomalies);
    const pb = reviewPriorityFromAnomalies(b.anomalies);
    const ra = REVIEW_PRIORITY_RANK[pa] ?? 99;
    const rb = REVIEW_PRIORITY_RANK[pb] ?? 99;
    return ra - rb;
  });
}

/**
 * Prefetch live anomaly scores for out-of-limit welds via the API directly
 * (avoids Redux takeLatest cancelling concurrent checks).
 */
export async function prefetchLiveAnomalyScores({
  rows,
  projectId,
  skipKeys,
  onProgress,
  onResult,
  onError,
  shouldContinue = () => true,
  concurrency = LIVE_ANOMALY_PREFETCH_CONCURRENCY,
  cap = WPS_BATCH_FETCH_CAP,
}: PrefetchLiveAnomalyScoresArgs): Promise<{ targetCount: number; loadedCount: number }> {
  const toFetch = sortFailsByPriority(rows.filter((r) => r.outcome === 'fail'))
    .filter((r) => {
      const key = rowKey(r);
      return Boolean(r.weldNumber && r.jobNumber) && !skipKeys.has(key);
    })
    .slice(0, cap);

  const total = toFetch.length;
  if (total === 0) {
    return { targetCount: 0, loadedCount: 0 };
  }

  void warmupScoringLambdas();
  if (!shouldContinue()) {
    return { targetCount: total, loadedCount: 0 };
  }

  let loaded = 0;
  await runWithConcurrency(toFetch, concurrency, async (row) => {
    if (!shouldContinue()) return;
    const weldKey = rowKey(row);
    try {
      const result = await checkAnomaly({
        weld_number: row.weldNumber,
        job_number: row.jobNumber,
        project_id: projectId,
      });
      if (!shouldContinue()) return;
      onResult(weldKey, result);
    } catch (err) {
      if (!shouldContinue()) return;
      const message =
        err instanceof Error
          ? err.message
          : typeof err === 'object' && err && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Live anomaly check failed';
      onError(weldKey, message);
    } finally {
      loaded += 1;
      onProgress?.(loaded, total);
    }
  });

  return { targetCount: total, loadedCount: loaded };
}
